import * as ReportActions from "src/app/store/report/actions";
import * as SurveyActions from "src/app/store/survey/actions";
import * as UserActions from "src/app/store/user/actions";
import * as FromReport from "src/app/store/report/selectors";
import * as FromSurvey from "src/app/store/survey/selectors";
import * as FromUser from "src/app/store/user/selectors";
import * as FromRouter from "src/app/store/router/selectors";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Observable } from "rxjs";
import { AppRoutes } from "src/app/constants";
import { RootState } from "src/app/store";
import { ActionButtonDto, CreateReportDto, ReportDto, SurveyDefinitionDto } from "@api";
import { first, skipWhile, withLatestFrom } from "rxjs/operators";
import { NavController } from "@ionic/angular";

@Component({
  selector: "app-report-thank-you",
  templateUrl: "./report-thank-you.page.html",
  styleUrls: ["./report-thank-you.page.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportThankYouPage implements OnInit {
  public routes: typeof AppRoutes = AppRoutes;
  public loading$: Observable<boolean> = this.store.select(FromReport.selectLoading);
  public slug$: Observable<string> = this.store.select(FromRouter.selectSurveySlug);
  public month$: Observable<string> = this.store.select(FromRouter.selectMonthFromQueryParams);
  public year$: Observable<string> = this.store.select(FromRouter.selectYearFromQueryParams);
  public actionButton$: Observable<ActionButtonDto> = this.store.select(FromUser.selectActionButton);
  public definition$: Observable<SurveyDefinitionDto>;
  public report$: Observable<ReportDto>;
  public breadcrumbs: { name: string; link?: string }[] = [
    { name: this.i18n.instant("DASHBOARD.BREADCRUMB_NAME"), link: `/${this.routes.home}` },
  ];

  constructor(
    private readonly store: Store<RootState>,
    private readonly i18n: TranslateService,
    private readonly navController: NavController,
    private readonly changeDetector: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.slug$.pipe(first()).subscribe(slug => {
      this.definition$ = this.store.select(FromSurvey.selectDefinitionBySlug(slug));
      this.store.dispatch(SurveyActions.getDefinitionBySlug({ slug }));
      this.store.dispatch(ReportActions.getReportResponses({ slug }));
      this.store.dispatch(UserActions.getActionButton());
    });

    this.definition$
      .pipe(
        skipWhile(definition => !definition),
        withLatestFrom(this.month$),
        withLatestFrom(this.year$),
        first(),
      )
      .subscribe(([[definition, month], year]) => {
        const dataSource = this.getDataSource(definition.slug);
        this.report$ = this.store.select(FromReport.selectBestReport(dataSource, +month, +year));
        this.breadcrumbs.push({ name: definition.name, link: `/${AppRoutes.reports}/${definition.slug}/${AppRoutes.history}` });
        this.changeDetector.detectChanges();
      });
  }

  public seeReport(): void {
    this.slug$.pipe(withLatestFrom(this.month$), withLatestFrom(this.year$), first()).subscribe(([[slug, month], year]) => {
      this.navController.navigateForward([AppRoutes.reports, slug, AppRoutes.history, AppRoutes.detail], { queryParams: { month, year } });
    });
  }

  public goBack(): void {
    this.slug$.pipe(first()).subscribe(slug => {
      this.navController.navigateForward([AppRoutes.reports, slug, AppRoutes.history]);
    });
  }

  private getDataSource(slug: string): CreateReportDto.DataSourceEnum {
    return slug === AppRoutes.successReport ? CreateReportDto.DataSourceEnum.SuccessReport : CreateReportDto.DataSourceEnum.BwaReport;
  }
}
